'use client'

import Script from 'next/script'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { waitForFreestarConsent } from './consent'
import { waitForFreestarPrivacyReady } from './privacy'
import { ensureFreestarGlobal, invokeFreestarSlot } from './runtime-state'
import { subscribeToFreestarSlotResult } from './slot-result'
import { trackFreestarEvent } from './telemetry'
import { MOBILE_STICKY_PUSHDOWN_NAVBAR_MOUNT_ID, type FreestarOpportunity, type FreestarSlotConfig } from './types'

const FREESTAR_SCRIPT_SRC = 'https://a.pub.network/giphy-com/pubfig.min.js'
const FREESTAR_CLS_CSS = 'https://a.pub.network/giphy-com/cls.css'
const FREESTAR_PRECONNECTS = [
    'https://a.pub.network',
    'https://b.pub.network',
    'https://c.pub.network',
    'https://d.pub.network',
    'https://btloader.com',
    'https://api.btloader.com',
]

function FreestarBannerSeparator() {
    return (
        <div
            id="freestar-banner-separator"
            style={{
                borderBottom: '2px solid #212121',
                width: '100vw',
                position: 'relative',
                left: '50%',
                right: '50%',
                marginLeft: '-50vw',
                marginRight: '-50vw',
            }}
        />
    )
}

function FreestarSlot({
    opportunity,
    slot,
    runtimeReady,
}: {
    opportunity: FreestarOpportunity
    slot: FreestarSlotConfig
    runtimeReady: boolean
}) {
    const invokedSlotId = useRef<string | undefined>(undefined)
    const opportunityRef = useRef(opportunity)
    const slotRef = useRef(slot)
    const [hasFilledLeaderboard, setHasFilledLeaderboard] = useState(false)
    const [mobilePushdownMount, setMobilePushdownMount] = useState<HTMLElement | null>(null)

    opportunityRef.current = opportunity
    slotRef.current = slot

    useEffect(() => {
        if (slot.kind !== 'mobile_pushdown') {
            return
        }
        setMobilePushdownMount(document.getElementById(MOBILE_STICKY_PUSHDOWN_NAVBAR_MOUNT_ID))
    }, [slot.kind])

    useEffect(() => {
        if (invokedSlotId.current === slot.slotId) {
            return
        }
        if (!runtimeReady) {
            return
        }
        if (slot.kind === 'mobile_pushdown' && !mobilePushdownMount) {
            return
        }

        const unsubscribe =
            slot.kind === 'leaderboard_atf'
                ? subscribeToFreestarSlotResult(slot.slotId, (result) => {
                      const currentOpportunity = opportunityRef.current
                      const currentSlot = slotRef.current
                      if (result === 'filled') {
                          setHasFilledLeaderboard(true)
                          trackFreestarEvent({
                              opportunity: currentOpportunity,
                              state: 'slot_filled',
                              slot: currentSlot,
                          })
                          return
                      }
                      trackFreestarEvent({ opportunity: currentOpportunity, state: 'slot_empty', slot: currentSlot })
                  })
                : undefined

        invokedSlotId.current = slot.slotId
        try {
            invokeFreestarSlot(slot)
            trackFreestarEvent({ opportunity, state: 'slot_invoked', slot })
        } catch (error) {
            trackFreestarEvent({ opportunity, state: 'runtime_error', reason: 'runtime_error', slot })
        }
        return unsubscribe
    }, [mobilePushdownMount, runtimeReady, slot.kind, slot.slotId])

    if (slot.kind === 'mobile_pushdown') {
        if (!runtimeReady || !mobilePushdownMount) {
            return null
        }

        return createPortal(
            <div className="flex min-h-[100px] w-full justify-center">
                <div
                    data-freestar-ad="__320x100"
                    id="giphy-com_mobile_sticky_pushdown-pushdown-cls"
                    style={{ textAlign: 'center' }}
                />
            </div>,
            mobilePushdownMount
        )
    }

    if (slot.kind !== 'leaderboard_atf') {
        return null
    }

    return (
        <>
            <div
                className="mx-auto hidden w-full justify-center overflow-hidden transition-[height,opacity] duration-200 motion-reduce:transition-none md:flex"
                data-freestar-leaderboard-state={hasFilledLeaderboard ? 'expanded' : 'collapsed'}
                id="freestar-leaderboard-container"
                style={{
                    height: hasFilledLeaderboard ? slot.reservedHeight : 0,
                    opacity: hasFilledLeaderboard ? 1 : 0,
                }}
            >
                <div
                    id={slot.slotId}
                    data-freestar-ad={runtimeReady ? slot.dataFreestarAd : undefined}
                    style={hasFilledLeaderboard ? { minWidth: slot.reservedWidth } : undefined}
                />
            </div>
            {hasFilledLeaderboard && <FreestarBannerSeparator />}
        </>
    )
}

function FreestarRuntime({ opportunity }: { opportunity: Extract<FreestarOpportunity, { status: 'ready' }> }) {
    const [privacyReady, setPrivacyReady] = useState(false)
    const trackedScriptLoaded = useRef(false)

    useEffect(() => {
        let active = true
        ensureFreestarGlobal()
        trackFreestarEvent({ opportunity, state: 'attempt' })

        waitForFreestarConsent().then((consent) => {
            if (!active) {
                return
            }
            if (consent.status === 'allowed') {
                waitForFreestarPrivacyReady(consent.didomi).then(() => {
                    if (active) {
                        setPrivacyReady(true)
                    }
                })
                return
            }
            trackFreestarEvent({ opportunity, state: consent.reason, reason: consent.reason })
        })

        return () => {
            active = false
        }
    }, [opportunity])

    return (
        <>
            {opportunity.slots.map(
                (slot) =>
                    (privacyReady || slot.kind === 'leaderboard_atf') && (
                        <FreestarSlot
                            key={slot.slotId}
                            opportunity={opportunity}
                            runtimeReady={privacyReady}
                            slot={slot}
                        />
                    )
            )}
            {privacyReady && (
                <>
                    {FREESTAR_PRECONNECTS.map((href) => (
                        <link key={href} rel="preconnect" href={href} />
                    ))}
                    <link rel="stylesheet" href={FREESTAR_CLS_CSS} />
                    <Script
                        id="freestar-pubfig"
                        src={FREESTAR_SCRIPT_SRC}
                        strategy="afterInteractive"
                        onReady={() => {
                            ensureFreestarGlobal().initCallbackCalled = true
                            if (!trackedScriptLoaded.current) {
                                trackedScriptLoaded.current = true
                                trackFreestarEvent({ opportunity, state: 'script_loaded' })
                            }
                        }}
                        onError={() => {
                            trackFreestarEvent({ opportunity, state: 'script_error', reason: 'script_error' })
                        }}
                    />
                </>
            )}
        </>
    )
}

export default function FreestarPlacement({ opportunity }: { opportunity?: FreestarOpportunity }) {
    const trackedSuppression = useRef<string | undefined>(undefined)

    useEffect(() => {
        if (!opportunity || opportunity.status !== 'suppressed') {
            return
        }
        if (opportunity.reason === 'backend_not_cleared') {
            return
        }
        const key = `${opportunity.context.view}:${opportunity.context.deviceType}:${opportunity.reason}`
        if (trackedSuppression.current === key) {
            return
        }
        trackedSuppression.current = key
        trackFreestarEvent({ opportunity, state: opportunity.reason, reason: opportunity.reason })
    }, [opportunity])

    if (!opportunity || opportunity.status !== 'ready') {
        return null
    }

    return <FreestarRuntime opportunity={opportunity} />
}
